const GENERIC_BRAND_SOURCE = 'myIPA-Website';
const PLACEMENT_WINDOW_SECONDS = 30;

type BasketLine = {
  isscript?: unknown;
  isgeneric?: unknown;
  istbc?: unknown;
  cardtype?: unknown;
  created_at?: string | null;
  prescriptionid?: unknown;
  basketliststatus?: unknown;
};

// orderData.created_at carries store-local digits behind a false 'Z' while basket lines carry UTC,
// so timestamps are compared as plain digits and never parsed in the browser's time zone.
const toDigitSeconds = (value: unknown): number | null => {
  const match = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2}):(\d{2})/.exec(String(value ?? '').trim());
  if (!match) {
    return null;
  }
  const [, year, month, day, hour, minute, second] = match.map(Number);
  return Date.UTC(year, month - 1, day, hour, minute, second) / 1000;
};

const isBinaryFlag = (value: unknown): boolean => value === 0 || value === 1 || value === '0' || value === '1';

const isCheckoutLine = (line: BasketLine, earliestSeconds: number): boolean => {
  if (Number(line.isscript) !== 1 || !isBinaryFlag(line.isgeneric) || !isBinaryFlag(line.istbc)) {
    return false;
  }
  const cardtype = String(line.cardtype ?? '').trim();
  if (cardtype === '' || /^\d+$/.test(cardtype)) {
    return false;
  }
  const lineSeconds = toDigitSeconds(line.created_at);
  return lineSeconds !== null && lineSeconds - earliestSeconds <= PLACEMENT_WINDOW_SECONDS;
};

export const genericBrandMark = (
  orderData: { Source?: string; created_at?: string | null } | null | undefined,
  line: BasketLine | null | undefined,
  allBasketLines: Array<BasketLine | null | undefined> | null | undefined,
  cutOver: string,
): 'Generic' | 'Brand' | null => {
  if (!line || orderData?.Source !== GENERIC_BRAND_SOURCE) {
    return null;
  }

  const orderSeconds = toDigitSeconds(orderData.created_at);
  const cutOverSeconds = toDigitSeconds(cutOver);
  if (orderSeconds === null || cutOverSeconds === null || orderSeconds < cutOverSeconds) {
    return null;
  }

  const basketLines = (allBasketLines ?? []).filter((basketLine): basketLine is BasketLine => !!basketLine);
  const earliestSeconds = Math.min(
    ...[line, ...basketLines].map(basketLine => toDigitSeconds(basketLine.created_at)).filter((s): s is number => s !== null),
  );
  if (!Number.isFinite(earliestSeconds)) {
    return null;
  }

  // A line the pharmacy edited is saved as a new row; the customer's choice stays on the replaced
  // checkout row of the same prescription in originalscriptslist.
  const prescriptionId = String(line.prescriptionid ?? '').trim();
  const source = isCheckoutLine(line, earliestSeconds)
    ? line
    : basketLines
        .filter(original =>
          Number(original.basketliststatus) === 0 &&
          prescriptionId !== '' &&
          String(original.prescriptionid ?? '').trim() === prescriptionId &&
          isCheckoutLine(original, earliestSeconds))
        .sort((a, b) => (toDigitSeconds(a.created_at) ?? 0) - (toDigitSeconds(b.created_at) ?? 0))[0];
  if (!source) {
    return null;
  }
  return Number(source.isgeneric) === 1 ? 'Generic' : 'Brand';
};
